import * as React from "react";
import { useEntityFuzzySearch } from "./useEntityFuzzySearch";
/** Items minus the caller's `excludeIds` — the rows the picker can actually offer. */ export function getCandidateItems(items, excludeIds) {
    if (!excludeIds || excludeIds.length === 0) return items;
    const block = new Set(excludeIds);
    return items.filter((i)=>!block.has(i._id));
}
/**
 * Whether the bucket (curated empty-query) view is worth showing: bucketing is
 * configured AND it either would render sections for these candidate items OR
 * there are no candidate items to fall back to (so its dedicated no-data empty
 * state still shows). When it reports no buckets but there ARE items, the
 * picker skips the empty recommendation screen and opens the all-list directly.
 * Shared by the body and the modal wrapper — both pass the same post-`excludeIds`
 * candidate list so they pick the same initial view.
 */ export function isBucketViewAvailable(bucketing, candidateItems) {
    if (!bucketing) return false;
    if (candidateItems.length === 0) return true;
    return bucketing.hasBuckets ? bucketing.hasBuckets(candidateItems) : true;
}
export function getByDottedPath(obj, path) {
    if (!obj || typeof obj !== "object") return undefined;
    return path.split(".").reduce((acc, key)=>acc && typeof acc === "object" ? acc[key] : undefined, obj);
}
function getItemNameForSort(item, config) {
    const field = config.matchInField?.primaryField ?? String(config.searchKeys[0]?.name ?? "");
    if (!field) return "";
    const value = getByDottedPath(item, field);
    return typeof value === "string" ? value : "";
}
/**
 * Encapsulates EntityPickerBody's state machine: controlled selection draft,
 * search query (with deferred value for snappy input), view-mode transitions,
 * facet/department filtering, and the derived fuzzy-search result lists.
 * Returns plain values + setters; rendering stays in the body component.
 */ export function useEntityPickerState({ items, selectedIds, mode, config, excludeIds, onDraftChange, onViewModeChange }) {
    // Body is fully controlled: `selectedIds` IS the draft.
    const draft = selectedIds;
    const setDraft = React.useCallback((next)=>{
        const value = typeof next === "function" ? next(draft) : next;
        onDraftChange?.(value);
    }, [
        draft,
        onDraftChange
    ]);
    const [query, setQuery] = React.useState("");
    const deferredQuery = React.useDeferredValue(query);
    const [activeFacet, setActiveFacet] = React.useState(null);
    const candidateItems = React.useMemo(()=>getCandidateItems(items, excludeIds), [
        items,
        excludeIds
    ]);
    const bucketViewAvailable = React.useMemo(()=>isBucketViewAvailable(config.bucketing, candidateItems), [
        config.bucketing,
        candidateItems
    ]);
    // The auto default follows `bucketViewAvailable`, which is derived from the
    // async-loaded picker suggestions. Freezing it in `useState` would strand the
    // picker in whatever view the loading snapshot implied (e.g. the all-list on a
    // cold cache, even once real recommendations resolve). So only the user's
    // explicit navigations (search / show-all / back-to-suggestions) are sticky;
    // until then the effective view tracks the reactive default.
    const autoViewMode = bucketViewAvailable ? "bucket" : "all";
    const [userViewMode, setUserViewMode] = React.useState(null);
    const viewMode = userViewMode ?? autoViewMode;
    const onViewModeChangeRef = React.useRef(onViewModeChange);
    onViewModeChangeRef.current = onViewModeChange;
    const setViewMode = React.useCallback((next)=>{
        setUserViewMode(next);
        onViewModeChangeRef.current?.(next);
    }, []);
    const byId = React.useMemo(()=>new Map(candidateItems.map((i)=>[
                i._id,
                i
            ])), [
        candidateItems
    ]);
    const departmentFilteredItems = React.useMemo(()=>{
        const filter = config.departmentFilter;
        if (!filter || !filter.selectedId) return candidateItems;
        return candidateItems.filter((i)=>{
            const value = getByDottedPath(i, filter.field);
            return value === filter.selectedId;
        });
    }, [
        candidateItems,
        config.departmentFilter
    ]);
    const showBucketedEmptyView = viewMode === "bucket" && bucketViewAvailable;
    const facetCounts = React.useMemo(()=>{
        if (!config.facet) return null;
        const field = config.facet.field;
        const counts = new Map();
        for (const item of departmentFilteredItems){
            const raw = item[field];
            if (typeof raw !== "string" || raw.length === 0) continue;
            counts.set(raw, (counts.get(raw) ?? 0) + 1);
        }
        return [
            ...counts.entries()
        ].sort((a, b)=>{
            if (b[1] !== a[1]) return b[1] - a[1];
            return a[0].localeCompare(b[0]);
        });
    }, [
        config.facet,
        departmentFilteredItems
    ]);
    // Department counts are computed off of `candidateItems` (un-department-
    // filtered) so each rail row shows the size of its hypothetical filter.
    const departmentCounts = React.useMemo(()=>{
        if (!config.departmentFilter) return null;
        const counts = new Map();
        for (const item of candidateItems){
            const value = getByDottedPath(item, config.departmentFilter.field);
            if (typeof value !== "string" || value.length === 0) continue;
            counts.set(value, (counts.get(value) ?? 0) + 1);
        }
        return counts;
    }, [
        candidateItems,
        config.departmentFilter
    ]);
    const visibleItems = React.useMemo(()=>{
        if (!activeFacet || !config.facet) return departmentFilteredItems;
        const field = config.facet.field;
        return departmentFilteredItems.filter((i)=>i[field] === activeFacet);
    }, [
        activeFacet,
        departmentFilteredItems,
        config.facet
    ]);
    const showNoData = showBucketedEmptyView && visibleItems.length === 0;
    const { results } = useEntityFuzzySearch({
        items: visibleItems,
        query: deferredQuery,
        keys: config.searchKeys
    });
    const draftSet = React.useMemo(()=>new Set(draft), [
        draft
    ]);
    // Sort the result list once per query/config change — partitioning into
    // selected/rest then preserves order without re-sorting on every toggle.
    const sortedResults = React.useMemo(()=>{
        if (deferredQuery.trim().length > 0) return results;
        return [
            ...results
        ].map((entry)=>({
                entry,
                name: getItemNameForSort(entry.item, config)
            })).sort((a, b)=>a.name.localeCompare(b.name)).map(({ entry })=>entry);
    }, [
        results,
        deferredQuery,
        config
    ]);
    const allListSelected = React.useMemo(()=>sortedResults.filter((r)=>draftSet.has(r.item._id)), [
        sortedResults,
        draftSet
    ]);
    const allListRest = React.useMemo(()=>sortedResults.filter((r)=>!draftSet.has(r.item._id)), [
        sortedResults,
        draftSet
    ]);
    const isSelected = React.useCallback((id)=>draftSet.has(id), [
        draftSet
    ]);
    const toggle = React.useCallback((id)=>{
        if (mode === "single") {
            setDraft([
                id
            ]);
            return;
        }
        setDraft((d)=>d.includes(id) ? d.filter((x)=>x !== id) : [
                ...d,
                id
            ]);
    }, [
        mode,
        setDraft
    ]);
    const showEmpty = !showBucketedEmptyView && results.length === 0;
    const handleBackToBuckets = React.useCallback(()=>{
        if (!bucketViewAvailable) return;
        setQuery("");
        setActiveFacet(null);
        config.departmentFilter?.onChange(null);
        setViewMode("bucket");
    }, [
        bucketViewAvailable,
        config.departmentFilter,
        setViewMode
    ]);
    return {
        draft,
        draftSet,
        setDraft,
        isSelected,
        toggle,
        query,
        setQuery,
        deferredQuery,
        trimmedQuery: deferredQuery.trim(),
        viewMode,
        setViewMode,
        bucketViewAvailable,
        activeFacet,
        setActiveFacet,
        candidateItems,
        byId,
        departmentFilteredItems,
        visibleItems,
        facetCounts,
        departmentCounts,
        results,
        sortedResults,
        allListSelected,
        allListRest,
        showBucketedEmptyView,
        showNoData,
        showEmpty,
        handleBackToBuckets
    };
}

//# sourceMappingURL=useEntityPickerState.js.map